// Action creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
import { ActionCreatorLoaderContext } from "../contexts/ActionCreatorLoaderContext";
// Constants
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { StoreLoaderContext } from "../contexts/StoreLoaderContext";
// Utilities
import { useNullSafeContext } from "@ms/planner-generic-controls/lib/local/hooks/useNullSafeContext";
import { useEffect } from "react";
import { useConfigProvider } from "./useConfigProvider";
/**
 * Hook to track view render state
 * @param viewId view Id
 * @param calculateViewRenderState The function to calculate the view render state
 */ export function useRenderTracker(viewId, calculateViewRenderState) {
    const storeLoader = useNullSafeContext(StoreLoaderContext);
    const acLoader = useNullSafeContext(ActionCreatorLoaderContext);
    const configProvider = useConfigProvider();
    /**
     * This effect will calculate the view render state on every render
     * and signal the performance collection action creator.
     *
     * Note: This check is run as an effect instead of within the render
     * loop to ensure that we signal the render after the render is complete
     * and the view is visible. This is important for accurate tracking.
     */ useEffect(()=>{
        const performanceCollectionActionCreator = acLoader.load(ActionCreator.PerformanceCollection);
        // Once the view is fully rendered we stop recalculating its state, but still settle any
        // interaction armed afterwards. completeInteractions is a no-op when nothing is pending.
        const currentState = storeLoader.load(Store.PerformanceCollection).getViewRenderState(viewId);
        if (currentState === ViewRenderState.All) {
            performanceCollectionActionCreator.completeInteractions(viewId, ViewRenderState.All);
            return;
        }
        const calculatedState = calculateViewRenderState(storeLoader, configProvider);
        performanceCollectionActionCreator.viewRender(viewId, calculatedState);
    });
    // Render state is monotonic and outlives the surface that produced it, so resetting on teardown is
    // what lets the same view be measured again. Anything still pending is logged as abandoned. Keyed on
    // primitives because `viewId` is rebuilt every render by its owner.
    const { name: trackedName, partitionKey: trackedPartitionKey } = viewId;
    useEffect(()=>()=>{
            acLoader.load(ActionCreator.PerformanceCollection).cancelInteractions({
                name: trackedName,
                partitionKey: trackedPartitionKey
            });
        }, [
        trackedName,
        trackedPartitionKey,
        acLoader
    ]);
}
